import { useEffect, useState } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Copy,
  MousePointerClick,
  UserPlus,
  ShoppingBag,
  DollarSign,
  BadgePercent,
  Loader2,
  Link as LinkIcon,
  ArrowRight,
} from "lucide-react";

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div
      className="rounded-sm border border-ink-100 bg-white p-5"
      data-testid={`partner-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2 text-ink-400 text-[10px] uppercase tracking-widestx font-mono">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="mt-2 text-3xl font-display font-extrabold text-ink-900 leading-none">
        {value}
      </div>
      {hint ? <div className="mt-1 text-xs text-ink-500">{hint}</div> : null}
    </div>
  );
}

const KIND_LABELS = {
  tier_upgrade: "Subscription",
  credit_pack: "Enrichment credits",
  email_pack: "Outreach credits",
  mobile_pack: "Mobile credits",
};

function describePolicy(p) {
  const mode = p?.discount_mode || "lifetime";
  if (mode === "lifetime") return "on every purchase, for life";
  if (mode === "first_purchase") return "on their first paid checkout only";
  if (mode === "subscription_months")
    return `on their first ${p.discount_duration_months || 3} months of subscription (credit / email / mobile packs full price)`;
  if (mode === "days_after_signup")
    return `on every purchase within ${p.discount_duration_days || 90} days of signup`;
  return "";
}

export default function PartnerDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discountInput, setDiscountInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get("/partner/me");
      setData(data);
      setDiscountInput(String(data.partner.discount_pct));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load partner data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-ink-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-ink-900">
          No partner record found
        </h1>
        <p className="mt-2 text-sm text-ink-500">
          Your account isn't linked to a partner. Ask your LeadIQ Pro account manager.
        </p>
      </div>
    );
  }

  const { partner, stats, conversions, share_url } = data;
  const cap = partner.discount_cap_pct;

  const copyShare = () => {
    navigator.clipboard.writeText(share_url).then(() => {
      toast.success("Share link copied to clipboard.");
    });
  };

  const saveDiscount = async () => {
    const pct = Number(discountInput);
    if (Number.isNaN(pct) || pct < 0) {
      toast.error("Enter a valid discount %.");
      return;
    }
    if (pct > cap) {
      toast.error(`Cannot exceed your cap of ${cap}%.`);
      return;
    }
    setSaving(true);
    try {
      const { data: resp } = await http.patch("/partner/discount", {
        discount_pct: pct,
      });
      setData((d) => ({ ...d, partner: resp.partner }));
      setDiscountInput(String(resp.partner.discount_pct));
      toast.success(`Discount updated to ${resp.partner.discount_pct}%.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update discount");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="partner-dashboard">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
          Partner program
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-ink-900 mt-1">
          {partner.name}
        </h1>
        <p className="text-ink-500 text-sm mt-1">
          Code <span className="font-mono text-ink-900">{partner.code}</span>
          {" · "}
          {partner.active ? (
            <span className="text-emerald-600">Active</span>
          ) : (
            <span className="text-rose-600">Paused</span>
          )}
          {" · "}
          <span className="text-ink-700" data-testid="partner-policy-summary">
            Discount applies <b>{describePolicy(partner)}</b>
          </span>
        </p>
      </div>

      {/* Share link card */}
      <div className="rounded-sm border border-ink-100 bg-white p-6 mb-6">
        <div className="flex items-center gap-2 text-ink-400 text-[10px] uppercase tracking-widestx font-mono mb-3">
          <LinkIcon className="w-3.5 h-3.5" />
          Your share link
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 font-mono text-sm bg-ink-50 border border-ink-100 rounded-sm px-3 py-2.5 truncate">
            {share_url}
          </div>
          <Button
            onClick={copyShare}
            className="shrink-0 gap-2"
            data-testid="partner-copy-share-link"
          >
            <Copy className="w-4 h-4" />
            Copy link
          </Button>
        </div>
        <p className="text-xs text-ink-500 mt-3">
          Anyone who visits LeadIQ Pro through this link gets{" "}
          <b>{partner.discount_pct}% off</b> — {describePolicy(partner)} — and
          we attribute every click, signup and payment back to you.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
        <Stat
          icon={MousePointerClick}
          label="Clicks"
          value={stats.clicks.toLocaleString()}
          hint="Unique link visits"
        />
        <Stat
          icon={UserPlus}
          label="Signups"
          value={stats.signups.toLocaleString()}
          hint="Accounts from your link"
        />
        <Stat
          icon={ShoppingBag}
          label="Conversions"
          value={stats.paid_conversions.toLocaleString()}
          hint="Paid purchases"
        />
        <Stat
          icon={DollarSign}
          label="Revenue"
          value={`$${stats.revenue_usd.toFixed(2)}`}
          hint="Net (post-discount)"
        />
        <Stat
          icon={BadgePercent}
          label="Discount given"
          value={`$${stats.discount_given_usd.toFixed(2)}`}
          hint="Savings you passed on"
        />
      </div>

      {/* Discount editor */}
      <div className="rounded-sm border border-ink-100 bg-white p-6 mb-8">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
          Your offered discount
        </div>
        <h3 className="font-display text-lg font-bold text-ink-900 mt-1">
          Set the % your audience gets
        </h3>
        <p className="text-sm text-ink-500 mt-1">
          Max allowed by LeadIQ Pro: <b>{cap}%</b>. Set 0% to disable the discount entirely (tracking still runs).
        </p>
        <div className="mt-4 flex items-end gap-3 max-w-xs">
          <div className="flex-1">
            <Label htmlFor="pct" className="text-xs font-mono uppercase tracking-widestx text-ink-500">
              Discount %
            </Label>
            <Input
              id="pct"
              type="number"
              min={0}
              max={cap}
              step="0.5"
              value={discountInput}
              onChange={(e) => setDiscountInput(e.target.value)}
              className="mt-1"
              data-testid="partner-discount-input"
            />
          </div>
          <Button
            onClick={saveDiscount}
            disabled={saving}
            className="gap-2"
            data-testid="partner-discount-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Save
          </Button>
        </div>
      </div>

      {/* Conversions table */}
      <div className="rounded-sm border border-ink-100 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
              Recent activity
            </div>
            <h3 className="font-display text-lg font-bold text-ink-900 mt-1">
              Latest paid conversions
            </h3>
          </div>
        </div>
        {conversions.length === 0 ? (
          <div className="text-sm text-ink-500 py-8 text-center border border-dashed border-ink-100 rounded-sm">
            No paid conversions yet — share your link and check back.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="partner-conversions-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widestx font-mono text-ink-400 border-b border-ink-100">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Product</th>
                  <th className="py-2 pr-3">Subtotal</th>
                  <th className="py-2 pr-3">Discount</th>
                  <th className="py-2 pr-3">Net</th>
                </tr>
              </thead>
              <tbody>
                {conversions.map((c) => (
                  <tr key={c.id} className="border-b border-ink-100/60">
                    <td className="py-2.5 pr-3 text-ink-700 font-mono text-xs">
                      {new Date(c.converted_at).toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-3 text-ink-900">
                      {KIND_LABELS[c.kind] || c.kind}
                    </td>
                    <td className="py-2.5 pr-3 text-ink-700">
                      ${Number(c.subtotal_usd || 0).toFixed(2)}
                    </td>
                    <td className="py-2.5 pr-3 text-brand-700">
                      −${Number(c.discount_amount_usd || 0).toFixed(2)} ({c.discount_pct}%)
                    </td>
                    <td className="py-2.5 pr-3 text-ink-900 font-semibold">
                      ${Number(c.net_amount_usd || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
